import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { parseAsUtc } from '../../../utils/dateUtils';

/**
 * ObjectiveProgressRenderer Component
 * Renders treatment objectives in note templates for progress documentation
 * 
 * IMPORTANT: This is for documentation only - progress updates are stored in the note,
 * not bidirectionally synced back to the treatment plan.
 */
function ObjectiveProgressRenderer({ value, onChange, fieldConfig }) {
  const { isDarkMode } = useDarkMode();
  const [objectives, setObjectives] = useState([]);

  useEffect(() => {
    if (Array.isArray(value)) {
      setObjectives(value.map(obj => ({
        ...obj,
        progress_status: obj.progress_status || '',
        progress_notes: obj.progress_notes || ''
      })));
    } else if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        setObjectives(Array.isArray(parsed) ? parsed.map(obj => ({
          ...obj,
          progress_status: obj.progress_status || '',
          progress_notes: obj.progress_notes || ''
        })) : []);
      } catch (e) {
        setObjectives([]);
      }
    } else {
      setObjectives([]);
    }
  }, [value]);

  const handleProgressChange = (index, field, newValue) => {
    const updated = [...objectives];
    updated[index] = {
      ...updated[index],
      [field]: newValue
    };
    setObjectives(updated);
    
    if (onChange) {
      onChange(updated);
    }
  };

  if (!objectives || objectives.length === 0) {
    return (
      <div className={`p-4 rounded border ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700 text-gray-400' 
          : 'bg-gray-50 border-gray-300 text-gray-600'
      }`}>
        <p className="text-sm text-center">
          No treatment objectives available for this patient.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`p-3 rounded ${
        isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'
      }`}>
        <p className="text-sm font-medium">
          📋 Progress Documentation
        </p>
        <p className="text-xs mt-1">
          Document progress on treatment objectives below. This information will be saved with this note only.
        </p>
      </div>

      <div className="space-y-3">
        {objectives.map((objective, index) => (
          <div
            key={objective.id || index}
            className={`p-4 rounded border ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-300'
            }`}
          >
            <div className="mb-3">
              <div className="flex justify-between items-start mb-2">
                <p className="font-medium text-sm">
                  {objective.objective_text || objective.description}
                </p>
                {objective.status && (
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      objective.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : objective.status === 'resolved'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {objective.status}
                  </span>
                )}
              </div>
              
              {objective.target_date && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Target: {parseAsUtc(objective.target_date).format('MMM D, YYYY')}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Progress Status
                </label>
                <select
                  value={objective.progress_status || ''}
                  onChange={(e) => handleProgressChange(index, 'progress_status', e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="">Not Addressed</option>
                  <option value="resolved">Resolved</option>
                  <option value="maintained">Maintained</option>
                  <option value="improving">Improving</option>
                  <option value="regressing">Regressing</option>
                  <option value="not_addressed">Not Addressed</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Progress Notes
                </label>
                <textarea
                  value={objective.progress_notes || ''}
                  onChange={(e) => handleProgressChange(index, 'progress_notes', e.target.value)}
                  rows={2}
                  placeholder="Document progress on this objective..."
                  className={`w-full px-3 py-2 text-sm rounded border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={`p-3 rounded text-xs ${
        isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-600'
      }`}>
        <p>
          <strong>Note:</strong> Progress documentation is saved with this note only. 
          To modify the treatment plan objectives themselves, use the Treatment Plan tab in the patient profile.
        </p>
      </div>
    </div>
  );
}

export default ObjectiveProgressRenderer;
